import { useEffect, useState } from 'react';

import { Divider, Text, type TypeSelectOption, dialogService, toastService } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';
import dayjs from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import {
  ClaimExchangeDetailModal,
  ClaimExchangeFilter,
  ClaimExchangeRegisterModal,
  ClaimExchangeTable,
} from '~/components/claim';
import { PageTitle } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import { OrderDetailModal } from '~/components/order';

import {
  useDeleteExchangeOrders,
  useGetExchangeOrders,
  useGetExchangeOrdersExchangeReceiptId,
  usePostExchangeOrdersExchangeReceiptIdOrder,
} from '~/hooks/claim';
import { useModal, useToggle } from '~/hooks/common';
import { useGetCodes, useGetOrderOutboundOrderCode } from '~/hooks/order';
import { useClaimStore, useUserInfoStore } from '~/store';
import { addIf, formatDate } from '~/utils';

import { BREAD_CRUMB, CLAIM, OPTION_ALL, ORDERS, PATH, TOAST_MESSAGE } from '~/constants';
import {
  CLAIM_RETURN_STATUS,
  INITIAL_CLAIM_PAGE_CURSOR,
  INITIAL_CLAIM_PAGE_SIZE,
  KEYWORD_TYPE,
  MAX_CLAIM_DATE_RANGE,
  MAX_CLAIM_KEYWORD_COUNT,
} from '~/constants/claim';

import type {
  TypeExchangeData,
  TypeExchangeDetailData,
  TypeExchangeSearchFilterValues,
  TypeExchangeSearchFilterValuesState,
  TypeGetExchangeOrdersParams,
} from '~/types/claim.types';
import type { TypeOrderOrderCode } from '~/types/order.types';

const DEFAULT_SEARCH_FILTER_VALUES: TypeExchangeSearchFilterValues = {
  keywordType: null,
  keyword: '',
  exchangeDate: { start: new Date(), end: new Date() },
  exchangeStatus: OPTION_ALL,
  exchangeType: OPTION_ALL,
  pagination: {
    currentPage: INITIAL_CLAIM_PAGE_CURSOR,
    totalCount: 0,
    pageSize: INITIAL_CLAIM_PAGE_SIZE,
  },
};

const DEFAULT_SEARCH_FILTER_VALUES_STATE: TypeExchangeSearchFilterValuesState = {
  keyword: 'default',
  exchangeDate: 'default',
};

const ClaimExchangePage = () => {
  const { requestCodes } = useGetCodes();
  const { requestExchangeOrders } = useGetExchangeOrders();
  const { requestDeleteExchangeOrders } = useDeleteExchangeOrders();
  const { requestExchangeOrdersDetail } = useGetExchangeOrdersExchangeReceiptId();
  const { requestOrderDetail } = useGetOrderOutboundOrderCode();
  const { resendExchangeOrders } = usePostExchangeOrdersExchangeReceiptIdOrder();

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  const [selectOptions, setSelectOptions] = useClaimStore(
    useShallow((state) => [state.selectOptions, state.actions.setSelectOptions])
  );
  const searchFilterOptions = {
    keyword: selectOptions.exchangeKeyword,
    exchangeStatus: [OPTION_ALL, ...selectOptions.returnStatus],
  };

  const [searchFilterValues, setSearchFilterValues] = useState<TypeExchangeSearchFilterValues>({
    ...DEFAULT_SEARCH_FILTER_VALUES,
  });
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<TypeExchangeSearchFilterValuesState>({
    ...DEFAULT_SEARCH_FILTER_VALUES_STATE,
  });
  const [exchangeData, setExchangeData] = useState<TypeExchangeData[] | null>(null);
  const [selectedItem, setSelectedItem] = useState<TypeExchangeData | null>(null);
  const [exchangeDetailData, setExchangeDetailData] = useState<TypeExchangeDetailData | null>(null);
  const [orderDetailsData, setOrderDetailsData] = useState<TypeOrderOrderCode | null>(null);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const exchangeRegisterModal = useModal({
    onConfirmCompleted: () => handleSearch(),
  });

  const exchangeDetailModal = useModal({
    onOpen: async (id?: string) => {
      if (!id) return;
      await requestExchangeOrdersDetail(
        { exchangeReceiptId: id },
        {
          onSuccess: (data) => setExchangeDetailData(data),
          onError: (error) => {
            toastService.addToast({
              type: 'error',
              message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
            });
          },
        }
      );
    },
  });

  const orderDetailModal = useModal({
    onOpen: async (orderCode?: string) => {
      if (!orderCode) return;

      await requestOrderDetail(
        { outboundOrderCode: orderCode },
        {
          onSuccess: (data) => setOrderDetailsData(data),
          onError: (error) => {
            toastService.addToast({
              type: 'error',
              message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
            });
          },
        }
      );
    },
  });

  useEffect(() => {
    requestCodes(
      {
        codeTypes: [
          'exchange-search-keyword',
          'return-status',
          'order-search-keyword',
          'order-search-date-type',
          'delivery-policy',
          'courier-code',
          'cluster-center',
          'pickup-type',
          'access-method',
          'return-reason',
        ],
      },
      {
        onSuccess: (codes) => {
          setSelectOptions({
            exchangeKeyword: codes.exchangeSearchKeyword,
            returnStatus: codes.returnStatus,
            orderKeyword: codes.orderSearchKeyword,
            orderDateType: codes.orderSearchDateType,
            deliveryPolicy: codes.deliveryPolicy,
            courierCode: codes.courierCode,
            clusterCenter: codes.clusterCenter,
            returnPlace: codes.pickUpType,
            accessMethod: codes.accessMethod,
            returnReason: codes.returnReason,
          });
          setSearchFilterValues({
            ...DEFAULT_SEARCH_FILTER_VALUES,
            keywordType: codes.exchangeSearchKeyword[0] ?? null,
          });
        },
      }
    );
  }, []);

  const handleChangeFilterValues = (values: Partial<TypeExchangeSearchFilterValues>) => {
    setSearchFilterValues((prev) => ({ ...prev, ...values }));
  };

  const handleChangeFilterValuesStatus = (status: Partial<TypeExchangeSearchFilterValuesState>) => {
    setSearchFilterValuesStatus((prev) => ({ ...prev, ...status }));
  };

  const handleSearch = (filterValues: TypeExchangeSearchFilterValues = searchFilterValues) => {
    if (
      filterValues.keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE ||
      filterValues.keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE ||
      filterValues.keywordType?.value === KEYWORD_TYPE.EXCHANGE_RECEIPT_ID
    ) {
      if (filterValues.keyword.split(',').length > MAX_CLAIM_KEYWORD_COUNT) {
        dialogService.addDialog({
          children: `${filterValues.keywordType?.text}는 최대 ${MAX_CLAIM_KEYWORD_COUNT}개까지 검색할 수 있습니다.`,
        });
        setSearchFilterValuesStatus((prev) => ({ ...prev, keyword: 'error' }));
        return;
      }
    }

    const isDateRangeEmpty = !filterValues.exchangeDate?.start && !filterValues.exchangeDate?.end;
    const dateDiff = dayjs(filterValues.exchangeDate?.end).diff(filterValues.exchangeDate?.start, 'day');
    const isDateLimitExceeded = dateDiff >= MAX_CLAIM_DATE_RANGE;

    if (isDateRangeEmpty || isDateLimitExceeded) {
      dialogService.addDialog({
        children: `최대 ${MAX_CLAIM_DATE_RANGE}일 기간으로 입력 후 검색해주세요.`,
      });
      setSearchFilterValuesStatus((prev) => ({ ...prev, exchangeDate: 'error' }));
      return;
    }

    const keywordArr = filterValues.keyword
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const params: TypeGetExchangeOrdersParams = {
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.OUTBOUND_ORDER_CODE && keywordArr,
        'orderCodes',
        keywordArr
      ),
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.CLIENT_ORDER_CODE && keywordArr,
        'clientOrderCodes',
        keywordArr
      ),
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.EXCHANGE_RECEIPT_ID && keywordArr,
        'exchangeReceiptIds',
        keywordArr
      ),
      ...addIf(
        filterValues.keywordType?.value === KEYWORD_TYPE.RECEIVER_NAME && keywordArr,
        'receiverName',
        filterValues.keyword
      ),
      ...addIf(formatDate(filterValues.exchangeDate?.start, 'YYYY-MM-DD'), 'receiptStartDate'),
      ...addIf(formatDate(filterValues.exchangeDate?.end, 'YYYY-MM-DD'), 'receiptEndDate'),
      ...addIf(filterValues.exchangeStatus?.value, 'returnReceiptStatuses'),
      ...addIf(filterValues.exchangeType?.value, 'exchangeTypes'),
      offset: filterValues.pagination.currentPage,
      limit: filterValues.pagination.pageSize,
    };

    requestExchangeOrders(params, {
      onSuccess: ({ data, pagination }) => {
        setExchangeData(data);
        setSelectedItem(null);

        setSearchFilterValues({
          ...filterValues,
          pagination: { ...filterValues.pagination, totalCount: pagination.totalCount },
        });
      },
      onError: (error) => {
        if (error?.code === ORDERS.INVALID_DATE_RANGE) {
          setSearchFilterValuesStatus((prev) => ({ ...prev, exchangeDate: 'error' }));
          dialogService.addDialog({
            children: `최대 ${MAX_CLAIM_DATE_RANGE}일 기간으로 입력 후 검색해주세요.`,
          });
          return;
        }

        toastService.addToast({
          type: 'error',
          message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });
      },
    });
  };

  const handleResetFilter = () => {
    setSearchFilterValues({
      ...DEFAULT_SEARCH_FILTER_VALUES,
      pagination: searchFilterValues.pagination,
      keywordType: searchFilterOptions.keyword[0] ?? null,
    });
    setSearchFilterValuesStatus({ ...DEFAULT_SEARCH_FILTER_VALUES_STATE });
  };

  const handleChangePageLimit = (value: TypeSelectOption<number>) => {
    handleSearch({
      ...searchFilterValues,
      pagination: { ...searchFilterValues.pagination, currentPage: INITIAL_CLAIM_PAGE_CURSOR, pageSize: value.value },
    });
  };

  const handleChangePage = (value: number) => {
    handleSearch({ ...searchFilterValues, pagination: { ...searchFilterValues.pagination, currentPage: value } });
  };

  const handleChangeRowSelection = (selectedRows: IRowNode<TypeExchangeData>[]) => {
    const selectedItem = selectedRows.map((row) => row.data).find((item) => !!item);
    setSelectedItem(selectedItem ?? null);
  };

  const handleClickCancelExchange = () => {
    dialogService.addDialog({
      type: 'confirm',
      children: '교환 취소 시 즉시 처리됩니다.\n취소하시겠습니까?',
      confirmText: '교환 취소',
      onConfirm: handleCancelExchange,
    });
  };

  const handleCancelExchange = () => {
    if (selectedItem?.status.code !== CLAIM_RETURN_STATUS.RETURN_RECEIPT_COMPLETE.value) {
      dialogService.addDialog({
        children: (
          <>
            <Text weight="semi-bold" size="large" element="span">
              [회수예정]
            </Text>{' '}
            상태에서만 교환 취소가 가능합니다.
          </>
        ),
      });
      return;
    }

    requestDeleteExchangeOrders(
      { exchangeReceiptId: selectedItem.exchangeReceiptId },
      {
        onSuccess: () => {
          toastService.addToast({
            message: '교환 취소되었습니다.',
          });
          handleSearch();
        },
        onError: (error) => {
          if (error?.code === CLAIM.ALREADY_PRE_EXCHANGE_DELIVERY_ING) {
            dialogService.addDialog({
              children: '현재 선교환 주문이 출고 진행중이므로\n교환 취소할 수 없습니다.',
            });
            return;
          }
          if (error?.code === CLAIM.ALREADY_RETURN_DELIVERY_ING) {
            dialogService.addDialog({
              children: '현재 배송사가 회수 진행중이므로\n교환 취소할 수 없습니다.',
            });
            return;
          }

          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };

  const getResendDialogContent = () => {
    if (hasPermission3PL) {
      return (
        <>
          <Text element="p" size="large">
            현재 교환상태{' '}
            <Text element="span" size="large" weight="semi-bold">
              [{selectedItem?.status.name}]
            </Text>{' '}
            입니다.
          </Text>
          <Text element="p" size="large">
            교환 재배송 수동 처리하시겠습니까?
          </Text>
          <Text element="p" size="large">
            검수 성공 시 자동으로 재배송 처리되며,
          </Text>
          <Text element="p" size="large">
            수동 재배송 처리 시 즉시 출고됩니다.
          </Text>
        </>
      );
    }
    if (hasPermissionNFA) {
      return (
        <>
          <Text element="p" size="large">
            {'교환 재배송 처리하시겠습니까?\n재배송 처리 후 출고요청번호를 눌러\n출고, 배송상태를 확인할 수 있습니다.'}
          </Text>
          <Text element="p" size="medium" color="tertiary">
            {'네이버 판매자센터에서 최대 8시간 내 재배송 처리 상태와 교환 배송 출고요청번호를 확인할 수 있습니다.'}
          </Text>
        </>
      );
    }
    return (
      <Text element="p" size="large">
        재배송 처리가 불가능한 유형입니다.
      </Text>
    );
  };

  const handleClickResendExchange = () => {
    if (!selectedItem) {
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: getResendDialogContent(),
      onConfirm: () =>
        resendExchangeOrders(
          { pathParams: { exchangeReceiptId: selectedItem.exchangeReceiptId } },
          {
            onSuccess: () => {
              toastService.addToast({
                message: '교환 재배송 처리되었습니다.',
              });
              handleSearch();
            },
            onError: (error) => {
              if (error?.code === CLAIM.ALREADY_EXCHANGE_RESEND_ING) {
                dialogService.addDialog({
                  children: '이미 교환 재배송 중인 건이 존재하여\n재배송 처리할 수 없습니다.',
                });
                return;
              }

              toastService.addToast({
                type: 'error',
                message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
              });
            },
          }
        ),
    });
  };

  return (
    <PageWrapper>
      <PageTitle
        title="교환 신청/조회"
        breadcrumbs={BREAD_CRUMB[PATH.CLAIM_EXCHANGE]}
        description={
          hasPermissionNFA ? (
            <Text size="medium" color="tertiary">
              검수 성공 시 교환 재배송 처리 버튼을 눌러 재배송 처리하실 수 있습니다.
            </Text>
          ) : hasPermission3PL ? (
            <Text size="medium" color="tertiary">
              검수 성공 시 자동으로 교환 재배송 처리가 진행됩니다. 교환 재배송 현황은 교환배송 출고요청번호를 눌러
              확인하실 수 있습니다.
            </Text>
          ) : undefined
        }
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <>
          <ClaimExchangeFilter
            options={searchFilterOptions}
            values={searchFilterValues}
            valuesState={searchFilterValuesStatus}
            onChange={handleChangeFilterValues}
            onChangeStatus={handleChangeFilterValuesStatus}
            onSearch={() =>
              handleSearch({
                ...searchFilterValues,
                pagination: {
                  ...searchFilterValues.pagination,
                  currentPage: INITIAL_CLAIM_PAGE_CURSOR,
                },
              })
            }
            onReset={handleResetFilter}
          />
          <Divider color="gray100" />
        </>
      )}
      <ClaimExchangeTable
        data={exchangeData}
        pagination={searchFilterValues.pagination}
        selectedItem={selectedItem}
        onChangePageLimit={handleChangePageLimit}
        onChangePage={handleChangePage}
        onChangeRowSelection={handleChangeRowSelection}
        onClickCancelExchange={handleClickCancelExchange}
        onClickResendExchange={handleClickResendExchange}
        onClickRegisterExchange={exchangeRegisterModal.handleOpenModal}
        onClickExchangeReceiptId={exchangeDetailModal.handleOpenModal}
        onClickExchangeOrderCode={orderDetailModal.handleOpenModal}
      />
      {exchangeRegisterModal.isOpen && (
        <ClaimExchangeRegisterModal
          isOpen={exchangeRegisterModal.isOpen}
          onCancel={exchangeRegisterModal.handleCloseModal}
          onConfirm={exchangeRegisterModal.handleConfirmModal}
        />
      )}
      {exchangeDetailModal.isOpen && (
        <ClaimExchangeDetailModal
          data={exchangeDetailData}
          isOpen={exchangeDetailModal.isOpen}
          onConfirm={exchangeDetailModal.handleConfirmModal}
          onClickOutboundOrderCode={(outboundOrderCode) => {
            exchangeDetailModal.handleCloseModal();
            orderDetailModal.handleOpenModal(outboundOrderCode);
          }}
        />
      )}
      {orderDetailModal.isOpen && (
        <OrderDetailModal
          data={orderDetailsData}
          open={orderDetailModal.isOpen}
          onConfirm={orderDetailModal.handleConfirmModal}
        />
      )}
    </PageWrapper>
  );
};

export default ClaimExchangePage;
